import React, { useEffect, useRef, useState } from 'react';
import { Form, Badge, Alert, Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAsterisk,
  faCalendarDay,
  faCheckCircle,
  faComment,
  faLightbulb,
  faPlus,
  faSpinner,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import {
  calculateEffectiveLength,
  truncateToEffectiveLimit,
} from '../../../lib/dbqTextLimitUtils';

/**
 * Shared DBQ form element rendering components.
 * Used by both DbqFormRenderer (admin preview) and UserDbqEditor (user submission).
 */

// Helper to check if a date field is a "Date Signed" field
// Auto-grow for long_text WITHOUT the collapse-remeasure page jump: resetting
// height to 'auto' momentarily shrinks a viewport-tall textarea to a few rows,
// and the browser's scroll anchoring yanks the page toward the collapsed field
// mid-keystroke (worst zoomed in, where Remarks towers past the viewport, and
// the caret ends up off-screen). While the field is FOCUSED we only ever GROW,
// which is measurable without collapsing; the full remeasure (the only way to
// shrink) runs on blur and on programmatic value swaps, with the window scroll
// pinned across the collapse.
const growDbqTextarea = (el) => {
  if (!el) return;
  if (el.scrollHeight > el.clientHeight) {
    el.style.height = `${el.scrollHeight}px`;
  }
};

const remeasureDbqTextarea = (el) => {
  if (!el) return;
  // Hold the wrapper's current space while the textarea collapses to 'auto':
  // without this the document momentarily shrinks by the field's height, the
  // browser clamps/anchors the scroll upward, and the user sees the page jump
  // to the field top and settle back. With the space held, layout never moves.
  const wrapper = el.parentElement;
  const prevMinHeight = wrapper ? wrapper.style.minHeight : '';
  if (wrapper) wrapper.style.minHeight = `${wrapper.offsetHeight}px`;
  const { scrollX, scrollY } = window;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
  if (wrapper) wrapper.style.minHeight = prevMinHeight;
  window.scrollTo({ left: scrollX, top: scrollY, behavior: 'instant' });
};

const isDateSignedField = (field) => {
  const idLower = (field.id || '').toLowerCase();
  const labelLower = (field.label || '').toLowerCase();

  // Check for common patterns in date signed fields
  const signedPatterns = [
    'date_signed',
    'signed_date',
    'signature_date',
    'signing_date',
    'date_of_signature',
  ];

  return signedPatterns.some(
    (pattern) =>
      idLower.includes(pattern) ||
      idLower.includes(pattern.replace(/_/g, '')) ||
      labelLower.includes('date signed') ||
      labelLower.includes('signature date') ||
      labelLower.includes('signing date'),
  );
};

// Helper to check if a date field is "Date of examination"
const isDateOfExaminationField = (field) => {
  const idLower = (field.id || '').toLowerCase();
  const labelLower = (field.label || '').toLowerCase();
  const examPatterns = [
    'date_of_examination',
    'date_of_exam',
    'exam_date',
    'examination_date',
  ];

  return (
    examPatterns.some(
      (pattern) =>
        idLower.includes(pattern) ||
        idLower.includes(pattern.replace(/_/g, '')),
    ) ||
    labelLower.includes('date of examination') ||
    labelLower.includes('date of exam')
  );
};

// Get today's date in YYYY-MM-DD format
const getTodayDateString = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Human-readable rendering of a stored value: raw option ids map to their
// labels, booleans become words, arrays join with commas. Returns null for
// "no answer" so the caller can show an empty message. Without a field
// definition (MSK supplemental call sites) values pass through as text.
// Exported for the QA Changes panel (prior vs current display).
export const formatSignedValueForDisplay = (value, field = null) => {
  if (value === null || value === undefined || value === '') return null;
  const optionLabel = (v) => {
    const options = Array.isArray(field?.options) ? field.options : [];
    // Templates key options by `id` (see the multi_select renderer); `value`
    // covers hand-built fixtures.
    const match = options.find((opt) => (opt?.id ?? opt?.value ?? opt) === v);
    return match?.label || String(v);
  };
  if (Array.isArray(value)) {
    return value.length ? value.map(optionLabel).join(', ') : null;
  }
  if (typeof value === 'boolean') {
    if (field?.field_type === 'checkbox')
      return value ? 'Checked' : 'Unchecked';
    return value ? 'Yes' : 'No';
  }
  if (
    field?.field_type === 'single_select' ||
    field?.field_type === 'multi_select'
  ) {
    return optionLabel(value);
  }
  return String(value);
};

// The per-field "Edited after signing" badge was removed 2026-08-04 (Nic:
// overkill). Edit visibility now lives in the QA Changes panel
// (DbqChangesPanel), which consumes the same _postSignEdits stamps.

// Render a single field
export const DbqField = ({
  field,
  instancePath = [],
  parentDisabled = false,
  formContext,
  renderWithGroupMap = {},
  isAdmin = false,
  canComment = false,
  canSuggest = false,
  onCommentClick,
  onSuggestionClick,
  activeCommentFieldId,
  activeSuggestionFieldId,
  requiredFields = [],
  onToggleRequired,
  onFieldChangeWithConfirmation,
}) => {
  const {
    buildFieldKey,
    getFieldValue,
    isVisible,
    isConditionallyRequired,
    isOptionConditionallyRequired,
    handleFieldChange,
    getFieldCommentCount,
    getFieldSuggestionCount,
  } = formContext;
  const prevDisabledRef = useRef(null);
  const textareaRef = useRef(null); // For long_text auto-resize
  const [pasteOverflow, setPasteOverflow] = useState(null);
  const [charsTrimmed, setCharsTrimmed] = useState(null);
  const charsTrimmedTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (charsTrimmedTimerRef.current)
        clearTimeout(charsTrimmedTimerRef.current);
    };
  }, []);
  const fieldKey = buildFieldKey(field.id, instancePath);
  const commentCount = getFieldCommentCount
    ? getFieldCommentCount(fieldKey)
    : 0;
  const suggestionCount = getFieldSuggestionCount
    ? getFieldSuggestionCount(fieldKey)
    : 0;
  const reviewCount = commentCount + suggestionCount;
  const isActiveComment = activeCommentFieldId === fieldKey;
  const isActiveSuggestion = activeSuggestionFieldId === fieldKey;
  const canShowEvidenceForField = formContext?.canShowEvidenceForField;
  const onShowEvidence = formContext?.onShowEvidence;
  const moEvidenceButtonLabel =
    formContext?.moEvidenceButtonLabel || 'Show Evidence';
  const isMoEvidenceButtonDisabled = Boolean(
    formContext?.isMoEvidenceButtonDisabled,
  );
  const canGenerateMoForField = formContext?.canGenerateMoForField;
  const onGenerateMo = formContext?.onGenerateMo;
  const moGenerateButtonLabel =
    formContext?.moGenerateButtonLabel || 'Generate MO';
  const isMoGenerateButtonDisabled = Boolean(
    formContext?.isMoGenerateButtonDisabled,
  );
  const suppressDisabledFieldValueClear = Boolean(
    formContext?.suppressDisabledFieldValueClear,
  );
  const getManagedMedicalOpinionRemarksState =
    formContext?.getManagedMedicalOpinionRemarksState;
  const openManagedMedicalOpinionRemarksEditor =
    formContext?.openManagedMedicalOpinionRemarksEditor;
  const getInlineSuggestionStateForField =
    formContext?.getInlineSuggestionStateForField;
  const onOpenInlineSuggestionDetail =
    formContext?.onOpenInlineSuggestionDetail;
  const onOpenInlineSuggestionEvidence =
    formContext?.onOpenInlineSuggestionEvidence;
  const onOpenInlineGenerationFailure =
    formContext?.onOpenInlineGenerationFailure;
  const onOpenInlineRejectedSuggestions =
    formContext?.onOpenInlineRejectedSuggestions;
  const useInlineSuggestionMode = Boolean(formContext?.useInlineSuggestionMode);
  const shouldShowEvidenceButton =
    typeof canShowEvidenceForField === 'function' &&
    canShowEvidenceForField(field, instancePath);
  const shouldShowGenerateMoButton =
    typeof canGenerateMoForField === 'function' &&
    canGenerateMoForField(field, instancePath);
  const handleReviewClick =
    useInlineSuggestionMode && typeof onCommentClick === 'function'
      ? onCommentClick
      : canSuggest && typeof onSuggestionClick === 'function'
        ? onSuggestionClick
        : onCommentClick || onSuggestionClick;
  const canReviewField = Boolean(
    handleReviewClick && (canComment || canSuggest),
  );
  const handleRequiredToggleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onToggleRequired(field.id);
  };

  const getReviewButtonTitle = () => {
    if (commentCount > 0 && suggestionCount > 0) {
      return `${commentCount} comment${commentCount > 1 ? 's' : ''}, ${suggestionCount} suggestion${suggestionCount > 1 ? 's' : ''}`;
    }
    if (suggestionCount > 0) {
      return `${suggestionCount} suggestion${suggestionCount > 1 ? 's' : ''}`;
    }
    if (commentCount > 0) {
      return `${commentCount} comment${commentCount > 1 ? 's' : ''}`;
    }
    return canSuggest ? 'Open comments and suggestions' : 'Add comment';
  };

  const isConditionMet = isVisible(field.visible_if, instancePath);
  // When visible_if is unmet, a field is kept visible-but-greyed (instead of
  // hidden) if it has a `code` OR explicitly opts in via `keep_visible_when_unmet`.
  // Default behavior is unchanged for any field that sets neither.
  const keepVisibleWhenUnmet = !!field.code || field.keep_visible_when_unmet;
  const shouldRender = isConditionMet || keepVisibleWhenUnmet;

  const isDisabled =
    parentDisabled || (!isConditionMet && keepVisibleWhenUnmet);
  const fieldValue = getFieldValue(field.id, instancePath);
  const inlineSuggestionState =
    typeof getInlineSuggestionStateForField === 'function'
      ? getInlineSuggestionStateForField(field, instancePath)
      : null;
  // Finalized = read-only everywhere (the server 403s writes too). A signed-
  // but-not-finalized DBQ no longer disables anything (2026-08-04 rework) -
  // edits are recorded server-side and surfaced in the QA Changes panel.
  // Routed through inputDisabled - NOT isDisabled - so the disabled-
  // transition effect below never clears field values.
  const finalizedLocked = Boolean(formContext?.finalizedLocked);
  const inputDisabled =
    isDisabled ||
    Boolean(inlineSuggestionState?.isLocked) ||
    finalizedLocked;
  // A field is required if it's in the requiredFields array (admin-marked), has required set in schema, or has required_if condition met
  const isAdminRequired = requiredFields.includes(field.id);
  const isConditionalRequired = isConditionallyRequired
    ? isConditionallyRequired(field, instancePath)
    : false;
  const isRequired = field.required || isAdminRequired || isConditionalRequired;

  // Clear field value when transitioning from enabled to disabled
  // Using JSON.stringify for instancePath to avoid reference comparison issues
  const instancePathKey = JSON.stringify(instancePath);
  useEffect(() => {
    // Only clear when transitioning from enabled (false) to disabled (true)
    // Skip on initial render (when prevDisabledRef.current is null)
    if (
      !suppressDisabledFieldValueClear &&
      prevDisabledRef.current === false &&
      isDisabled === true
    ) {
      // Clear the field value based on field type
      if (field.field_type === 'multi_select') {
        handleFieldChange(field.id, [], instancePath);
      } else {
        // null, not undefined: undefined keys drop out of the JSON save
        // payload, so the server would silently keep the old value.
        handleFieldChange(field.id, null, instancePath);
      }
    }
    prevDisabledRef.current = isDisabled;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDisabled,
    field.id,
    field.field_type,
    instancePathKey,
    handleFieldChange,
    suppressDisabledFieldValueClear,
  ]);

  // Auto-resize textarea when value changes programmatically (e.g., from MO insertion)
  // This hook must be at component level, not inside renderInput switch case
  // (it also fires per keystroke, so the focused path must never collapse).
  useEffect(() => {
    if (field.field_type === 'long_text' && textareaRef.current) {
      const el = textareaRef.current;
      if (document.activeElement === el) growDbqTextarea(el);
      else remeasureDbqTextarea(el);
    }
  }, [fieldValue, field.field_type]);

  if (!shouldRender) {
    return null;
  }

  const renderInput = () => {
    switch (field.field_type) {
      case 'yes_no': {
        // Check if custom options with required_if are provided
        const yesOption = field.options?.find((o) => o.id === 'yes');
        const noOption = field.options?.find((o) => o.id === 'no');
        const yesRequired =
          yesOption?.required_if &&
          isOptionConditionallyRequired?.(yesOption, instancePath);
        const noRequired =
          noOption?.required_if &&
          isOptionConditionallyRequired?.(noOption, instancePath);
        // Convert boolean fieldValue to string for confirmation option checking
        const currentOptionId =
          fieldValue === true ? 'yes' : fieldValue === false ? 'no' : null;
        const currentIsConfirmationOption =
          currentOptionId &&
          field.confirmation?.options?.includes(currentOptionId);

        return (
          <div className="d-flex gap-3">
            <Form.Check
              type="radio"
              id={`${fieldKey}_yes`}
              name={fieldKey}
              label={
                yesRequired ? (
                  <span
                    className={
                      yesRequired && fieldValue !== true ? 'text-danger' : ''
                    }
                  >
                    {yesOption?.label || 'Yes'}
                    <span className="text-danger ms-1 fw-bold">*</span>
                  </span>
                ) : (
                  yesOption?.label || 'Yes'
                )
              }
              value="true"
              checked={fieldValue === true}
              onClick={() => {
                // If already selected, clear the selection (toggle off).
                // null, not undefined: undefined keys drop out of the JSON
                // save payload, so the server would silently keep the old
                // value (and post-sign, its stamp).
                if (fieldValue === true) {
                  handleFieldChange(field.id, null, instancePath);
                  return;
                }
                // Show confirmation if selecting a confirmation option AND current value is not a confirmation option
                const needsConfirmation =
                  field.confirmation?.options?.includes('yes') &&
                  !currentIsConfirmationOption;
                if (needsConfirmation && onFieldChangeWithConfirmation) {
                  onFieldChangeWithConfirmation(
                    field,
                    'yes',
                    instancePath,
                    true,
                  );
                } else {
                  handleFieldChange(field.id, true, instancePath);
                }
              }}
              onChange={() => {}}
              className="custom-radio"
              disabled={inputDisabled}
            />
            <Form.Check
              type="radio"
              id={`${fieldKey}_no`}
              name={fieldKey}
              label={
                noRequired ? (
                  <span
                    className={
                      noRequired && fieldValue !== false ? 'text-danger' : ''
                    }
                  >
                    {noOption?.label || 'No'}
                    <span className="text-danger ms-1 fw-bold">*</span>
                  </span>
                ) : (
                  noOption?.label || 'No'
                )
              }
              value="false"
              checked={fieldValue === false}
              onClick={() => {
                // If already selected, clear the selection (toggle off).
                // null, not undefined - see the yes toggle above.
                if (fieldValue === false) {
                  handleFieldChange(field.id, null, instancePath);
                  return;
                }
                // Show confirmation if selecting a confirmation option AND current value is not a confirmation option
                const needsConfirmation =
                  field.confirmation?.options?.includes('no') &&
                  !currentIsConfirmationOption;
                if (needsConfirmation && onFieldChangeWithConfirmation) {
                  onFieldChangeWithConfirmation(
                    field,
                    'no',
                    instancePath,
                    false,
                  );
                } else {
                  handleFieldChange(field.id, false, instancePath);
                }
              }}
              onChange={() => {}}
              className="custom-radio"
              disabled={inputDisabled}
            />
          </div>
        );
      }

      case 'single_select': {
        // Check if current value is already a confirmation option
        const currentIsConfirmationOption =
          fieldValue && field.confirmation?.options?.includes(fieldValue);

        return (
          <div className="d-flex flex-column gap-2">
            {field.options?.map((option) => {
              const optionRequired =
                option.required_if &&
                isOptionConditionallyRequired?.(option, instancePath);
              return (
                <div key={option.id}>
                  <Form.Check
                    type="radio"
                    id={`${fieldKey}_${option.id}`}
                    name={fieldKey}
                    label={
                      optionRequired ? (
                        <span
                          className={
                            optionRequired && fieldValue !== option.id
                              ? 'text-danger'
                              : ''
                          }
                        >
                          {option.label}
                          <span className="text-danger ms-1 fw-bold">*</span>
                        </span>
                      ) : (
                        option.label
                      )
                    }
                    value={option.id}
                    checked={fieldValue === option.id}
                    onClick={() => {
                      // If already selected, clear the selection (toggle off).
                      // null, not undefined: undefined keys drop out of the
                      // JSON save payload, so the server would silently keep
                      // the old value (and post-sign, its stamp).
                      if (fieldValue === option.id) {
                        handleFieldChange(field.id, null, instancePath);
                        return;
                      }
                      // Show confirmation if selecting a confirmation option AND current value is not a confirmation option
                      const needsConfirmation =
                        field.confirmation?.options?.includes(option.id) &&
                        !currentIsConfirmationOption;
                      if (needsConfirmation && onFieldChangeWithConfirmation) {
                        onFieldChangeWithConfirmation(
                          field,
                          option.id,
                          instancePath,
                        );
                      } else {
                        handleFieldChange(field.id, option.id, instancePath);
                      }
                    }}
                    onChange={() => {}} // Handled by onClick for toggle-off support
                    className="custom-radio"
                    disabled={inputDisabled}
                  />
                  {option.description && (
                    <div className="dbq-option-description">
                      {option.description}
                    </div>
                  )}
                  {option.child_elements && fieldValue === option.id && (
                    <div
                      className="ms-4 mt-3 ps-3 border-start border-primary animate-fade-in"
                      style={{ animation: 'fadeIn 0.3s ease-in' }}
                    >
                      {option.child_elements.map((child) => (
                        <DbqElement
                          key={child.id}
                          element={child}
                          instancePath={instancePath}
                          skipRenderWithGroup={false}
                          parentDisabled={isDisabled}
                          formContext={formContext}
                          renderWithGroupMap={renderWithGroupMap}
                          isAdmin={isAdmin}
                          canComment={canComment}
                          canSuggest={canSuggest}
                          onCommentClick={onCommentClick}
                          onSuggestionClick={onSuggestionClick}
                          activeCommentFieldId={activeCommentFieldId}
                          activeSuggestionFieldId={activeSuggestionFieldId}
                          requiredFields={requiredFields}
                          onToggleRequired={onToggleRequired}
                          onFieldChangeWithConfirmation={
                            onFieldChangeWithConfirmation
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      case 'multi_select': {
        return (
          <div className="d-flex flex-column gap-2">
            {field.options?.map((option) => {
              const isChecked =
                Array.isArray(fieldValue) && fieldValue.includes(option.id);
              const optionRequired =
                option.required_if &&
                isOptionConditionallyRequired?.(option, instancePath);
              return (
                <div key={option.id}>
                  <Form.Check
                    type="checkbox"
                    id={`${fieldKey}_${option.id}`}
                    label={
                      optionRequired ? (
                        <span
                          className={
                            optionRequired && !isChecked ? 'text-danger' : ''
                          }
                        >
                          {option.label}
                          <span className="text-danger ms-1 fw-bold">*</span>
                        </span>
                      ) : (
                        option.label
                      )
                    }
                    checked={isChecked}
                    onChange={() => {
                      const newValue = isChecked
                        ? fieldValue.filter((v) => v !== option.id)
                        : [...(fieldValue || []), option.id];
                      // Child fields are cleared automatically by handleFieldChange when unchecking
                      handleFieldChange(field.id, newValue, instancePath);
                    }}
                    className="custom-checkbox"
                    disabled={inputDisabled}
                  />
                  {option.description && (
                    <div className="dbq-option-description">
                      {option.description}
                    </div>
                  )}
                  {option.child_elements && isChecked && (
                    <div
                      className="ms-4 mt-3 ps-3 border-start border-primary animate-fade-in"
                      style={{ animation: 'fadeIn 0.3s ease-in' }}
                    >
                      {option.child_elements.map((child) => (
                        <DbqElement
                          key={child.id}
                          element={child}
                          instancePath={instancePath}
                          skipRenderWithGroup={false}
                          parentDisabled={isDisabled}
                          formContext={formContext}
                          renderWithGroupMap={renderWithGroupMap}
                          isAdmin={isAdmin}
                          canComment={canComment}
                          canSuggest={canSuggest}
                          onCommentClick={onCommentClick}
                          onSuggestionClick={onSuggestionClick}
                          activeCommentFieldId={activeCommentFieldId}
                          activeSuggestionFieldId={activeSuggestionFieldId}
                          requiredFields={requiredFields}
                          onToggleRequired={onToggleRequired}
                          onFieldChangeWithConfirmation={
                            onFieldChangeWithConfirmation
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      case 'checkbox':
        return (
          <Form.Check
            type="checkbox"
            id={`${fieldKey}_checkbox`}
            checked={fieldValue === true}
            onChange={(e) =>
              handleFieldChange(field.id, e.target.checked, instancePath)
            }
            className="custom-checkbox"
            disabled={inputDisabled}
          />
        );

      case 'short_text':
        return (
          <Form.Control
            type="text"
            value={fieldValue || ''}
            onChange={(e) =>
              handleFieldChange(field.id, e.target.value, instancePath)
            }
            placeholder={field.description || ''}
            required={isRequired}
            maxLength={field.validation?.max_length}
            className="modern-input"
            disabled={inputDisabled}
          />
        );

      case 'long_text': {
        const maxLength = field.validation?.max_length;
        const charsPerLine = field.validation?.chars_per_line;
        const managedMedicalOpinionRemarksState =
          typeof getManagedMedicalOpinionRemarksState === 'function'
            ? getManagedMedicalOpinionRemarksState(field, instancePath)
            : null;
        const isManagedMedicalOpinionRemarks = Boolean(
          managedMedicalOpinionRemarksState?.isManaged,
        );
        // Use effective length (newlines cost rest of line) when chars_per_line is available
        const currentLength = charsPerLine
          ? calculateEffectiveLength(fieldValue || '', charsPerLine)
          : (fieldValue || '').length;
        const remaining = maxLength ? maxLength - currentLength : null;
        const isNearLimit = remaining !== null && remaining < 100;
        const isAtLimit = remaining !== null && remaining <= 0;
        const handleOpenManagedRemarksEditor = () => {
          if (
            !isManagedMedicalOpinionRemarks ||
            typeof openManagedMedicalOpinionRemarksEditor !== 'function'
          ) {
            return;
          }

          openManagedMedicalOpinionRemarksEditor(field, instancePath);
        };

        // Intercept paste before the browser inserts text
        const handlePaste = (e) => {
          if (!maxLength) return;
          const pastedText = (e.clipboardData?.getData('text') || '')
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n');
          if (!pastedText) return;

          // Build what the value would be after paste (normalize existing value too)
          const textarea = e.target;
          const normalizedFieldValue = (fieldValue || '')
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n');
          const before = normalizedFieldValue.slice(0, textarea.selectionStart);
          const after = normalizedFieldValue.slice(textarea.selectionEnd);
          const newValue = before + pastedText + after;

          const newEffectiveLength = charsPerLine
            ? calculateEffectiveLength(newValue, charsPerLine)
            : newValue.length;

          if (newEffectiveLength > maxLength) {
            e.preventDefault(); // Stop browser from inserting the text
            const over = newEffectiveLength - maxLength;
            setPasteOverflow({
              over,
              pendingValue: newValue,
              fieldId: field.id,
              instancePath,
              charsPerLine,
              maxLength,
            });
          }
        };

        // Handler for typed input (paste that fits goes through onChange normally)
        const handleTextChange = (e) => {
          const newValue = e.target.value;
          const newEffectiveLength = charsPerLine
            ? calculateEffectiveLength(newValue, charsPerLine)
            : newValue.length;

          // Safety net — block if over limit (e.g. drag-drop text)
          if (maxLength && newEffectiveLength > maxLength) {
            return;
          }

          handleFieldChange(field.id, newValue, instancePath);
          // Auto-resize (grow-only while typing — see growDbqTextarea)
          growDbqTextarea(e.target);
        };

        const handlePasteAnyway = () => {
          if (!pasteOverflow) return;
          const truncated = truncateToEffectiveLimit(
            pasteOverflow.pendingValue,
            pasteOverflow.maxLength,
            pasteOverflow.charsPerLine,
          );
          const originalLength = pasteOverflow.charsPerLine
            ? calculateEffectiveLength(
                pasteOverflow.pendingValue,
                pasteOverflow.charsPerLine,
              )
            : pasteOverflow.pendingValue.length;
          const truncatedLength = pasteOverflow.charsPerLine
            ? calculateEffectiveLength(truncated, pasteOverflow.charsPerLine)
            : truncated.length;
          const removed = originalLength - truncatedLength;
          handleFieldChange(field.id, truncated, instancePath);
          setPasteOverflow(null);
          if (removed > 0) {
            setCharsTrimmed(removed);
            if (charsTrimmedTimerRef.current)
              clearTimeout(charsTrimmedTimerRef.current);
            charsTrimmedTimerRef.current = setTimeout(
              () => setCharsTrimmed(null),
              8000,
            );
          }
          // Auto-resize after value update (modal path, field unfocused —
          // full remeasure with scroll pinned)
          setTimeout(() => {
            remeasureDbqTextarea(textareaRef.current);
          }, 0);
        };

        return (
          <div className="dbq-long-text-wrapper">
            <Form.Control
              ref={textareaRef}
              as="textarea"
              rows={4}
              value={fieldValue || ''}
              onChange={
                isManagedMedicalOpinionRemarks ? undefined : handleTextChange
              }
              onPaste={isManagedMedicalOpinionRemarks ? undefined : handlePaste}
              onInput={(e) => {
                if (isManagedMedicalOpinionRemarks) return;
                growDbqTextarea(e.target);
              }}
              onBlur={(e) => {
                if (isManagedMedicalOpinionRemarks) return;
                remeasureDbqTextarea(e.target);
              }}
              onClick={handleOpenManagedRemarksEditor}
              placeholder={field.description || ''}
              required={isRequired}
              maxLength={charsPerLine ? undefined : maxLength}
              className={`modern-input dbq-auto-resize ${isNearLimit ? 'dbq-near-limit' : ''} ${isAtLimit ? 'dbq-at-limit' : ''}`}
              disabled={inputDisabled}
              readOnly={isManagedMedicalOpinionRemarks}
              style={
                isManagedMedicalOpinionRemarks
                  ? { cursor: 'pointer' }
                  : undefined
              }
            />
            {maxLength && (
              <div
                className={`dbq-char-count ${isNearLimit ? 'text-warning' : ''} ${isAtLimit ? 'text-danger' : 'text-muted'}`}
              >
                {currentLength.toLocaleString()} / {maxLength.toLocaleString()}{' '}
                characters
                {isNearLimit && !isAtLimit && (
                  <span className="ms-1">({remaining} remaining)</span>
                )}
                {isAtLimit && (
                  <span className="ms-1 fw-bold">(limit reached)</span>
                )}
              </div>
            )}
            {charsTrimmed && (
              <div className="text-muted small mt-1">
                {charsTrimmed.toLocaleString()} characters were removed to fit
                within the limit.
              </div>
            )}
            <Modal
              show={Boolean(pasteOverflow)}
              onHide={() => setPasteOverflow(null)}
              centered
            >
              <Modal.Body className="py-4">
                <p className="mb-2">
                  Content exceeds the character limit by{' '}
                  <strong>{pasteOverflow?.over?.toLocaleString()}</strong>{' '}
                  characters.
                </p>
                <p className="mb-0 text-muted small">
                  Clicking &quot;Paste Anyway&quot; will trim the excess content
                  to fit.
                </p>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPasteOverflow(null)}
                >
                  Close
                </Button>
                <Button variant="primary" size="sm" onClick={handlePasteAnyway}>
                  Paste Anyway
                </Button>
              </Modal.Footer>
            </Modal>
            {isManagedMedicalOpinionRemarks && (
              <div className="mt-2 d-flex flex-column gap-2">
                <div className="small text-muted">
                  {managedMedicalOpinionRemarksState?.helpText ||
                    (managedMedicalOpinionRemarksState?.isTruncated
                      ? 'This field shows the DBQ-safe truncated version. Edit the full Medical Opinion separately and it will regenerate the PDF.'
                      : 'This remarks field is managed through the full Medical Opinion editor.')}
                </div>
                <div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleOpenManagedRemarksEditor}
                    disabled={inputDisabled}
                  >
                    {managedMedicalOpinionRemarksState?.buttonLabel ||
                      'Edit Full Medical Opinion'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'number':
        return (
          <Form.Control
            type="number"
            value={fieldValue || ''}
            onChange={(e) =>
              handleFieldChange(field.id, e.target.value, instancePath)
            }
            placeholder={field.description || ''}
            required={isRequired}
            min={field.validation?.min}
            max={field.validation?.max}
            className="modern-input"
            disabled={inputDisabled}
          />
        );

      case 'date': {
        const isSignedDate = isDateSignedField(field);
        const isExaminationDate = isDateOfExaminationField(field);
        const showTodayShortcut = isSignedDate || isExaminationDate;
        const todayDate = getTodayDateString();
        const showExamDatePlaceholderIfEmpty = Boolean(
          formContext?.showExamDatePlaceholderIfEmpty,
        );
        const showProviderPendingPlaceholder =
          isExaminationDate && showExamDatePlaceholderIfEmpty && !fieldValue;

        return (
          <div className="d-flex align-items-center gap-2">
            <Form.Control
              type={showProviderPendingPlaceholder ? 'text' : 'date'}
              value={
                showProviderPendingPlaceholder
                  ? 'To be filled in by the provider'
                  : fieldValue || ''
              }
              onChange={(e) =>
                showProviderPendingPlaceholder
                  ? undefined
                  : handleFieldChange(field.id, e.target.value, instancePath)
              }
              required={isRequired}
              className="modern-input"
              disabled={inputDisabled}
              min={isSignedDate ? todayDate : undefined}
              style={{ flex: 1 }}
              readOnly={showProviderPendingPlaceholder}
            />
            {showTodayShortcut && !showProviderPendingPlaceholder && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() =>
                  handleFieldChange(field.id, todayDate, instancePath)
                }
                disabled={inputDisabled}
                className="dbq-set-today-btn"
                title="Set to today's date"
              >
                <FontAwesomeIcon icon={faCalendarDay} className="me-1" />
                {isExaminationDate && !isSignedDate ? 'Today' : 'Set Today'}
              </Button>
            )}
          </div>
        );
      }

      default:
        return (
          <Form.Control
            type="text"
            value={fieldValue || ''}
            disabled
            className="modern-input"
          />
        );
    }
  };

  return (
    <Form.Group
      className={`mb-4 dbq-field-group ${isDisabled ? 'opacity-75' : ''} ${isActiveComment || isActiveSuggestion ? 'dbq-field-active-comment' : ''} ${inlineSuggestionState?.isAccepted ? 'dbq-field-inline-accepted' : ''} ${inlineSuggestionState?.hasMissingDetail ? 'dbq-field-inline-missing-detail' : ''}`}
      key={fieldKey}
      data-field-id={fieldKey}
    >
      <div className="form-label d-flex align-items-center mb-2">
        {field.code && (
          <Badge bg="primary" className="me-2 rounded-pill px-2">
            {field.code}
          </Badge>
        )}
        <span className="fw-medium">{field.label}</span>
        {isRequired && <span className="text-danger ms-1">*</span>}
        {isAdmin && onToggleRequired && (
          <button
            type="button"
            className={`dbq-required-toggle ms-2 ${isAdminRequired ? 'is-required' : ''}`}
            onClick={handleRequiredToggleClick}
            title={isAdminRequired ? 'Remove required' : 'Mark as required'}
          >
            <FontAwesomeIcon icon={faAsterisk} />
          </button>
        )}
        {shouldShowGenerateMoButton && typeof onGenerateMo === 'function' && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm ms-2 py-0 px-2"
            onClick={() => onGenerateMo(field.id)}
            disabled={isMoGenerateButtonDisabled}
            title="Generate or regenerate Medical Opinion"
          >
            {moGenerateButtonLabel}
          </button>
        )}
        {shouldShowEvidenceButton && typeof onShowEvidence === 'function' && (
          <button
            type="button"
            className="btn btn-outline-info btn-sm ms-2 py-0 px-2"
            onClick={() => onShowEvidence(field.id)}
            disabled={isMoEvidenceButtonDisabled}
            title="View Medical Opinion evidence"
          >
            {moEvidenceButtonLabel}
          </button>
        )}
        {canReviewField && (
          <button
            type="button"
            className={`dbq-comment-btn ms-2 ${reviewCount > 0 ? 'has-comments' : ''}`}
            onClick={() => handleReviewClick(fieldKey, field.label)}
            title={getReviewButtonTitle()}
          >
            <FontAwesomeIcon icon={faComment} />
            {reviewCount > 0 && (
              <span className="dbq-comment-badge">{reviewCount}</span>
            )}
          </button>
        )}
        {inlineSuggestionState && (
          <>
            {inlineSuggestionState.isAiGenerated ? (
              <span className="dbq-inline-suggestion-badge dbq-inline-suggestion-badge--ai ms-2">
                <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                AI
              </span>
            ) : null}
            {inlineSuggestionState.isAccepted ? (
              <span className="dbq-inline-suggestion-badge dbq-inline-suggestion-badge--accepted ms-2">
                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                Accepted
              </span>
            ) : null}
            {inlineSuggestionState.isLoading ? (
              <span className="dbq-inline-suggestion-badge dbq-inline-suggestion-badge--loading ms-2">
                <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                AI loading
              </span>
            ) : null}
            {inlineSuggestionState.isNotice &&
            !inlineSuggestionState.isLoading ? (
              <span className="dbq-inline-suggestion-badge dbq-inline-suggestion-badge--notice ms-2">
                <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                AI unavailable
              </span>
            ) : null}
          </>
        )}
      </div>
      {(inlineSuggestionState?.isAiGenerated &&
        !inlineSuggestionState.isNotice) ||
      inlineSuggestionState?.hasGenerationFailure ||
      inlineSuggestionState?.hasRejectedSuggestions ? (
        <div className="dbq-inline-suggestion-actions">
          {inlineSuggestionState.hasGenerationFailure ? (
            <button
              type="button"
              className="dbq-inline-suggestion-action-btn dbq-inline-suggestion-action-btn--failure"
              onClick={() => onOpenInlineGenerationFailure?.(fieldKey)}
              title="View why AI generation failed for this field"
            >
              Generation Failure
            </button>
          ) : null}
          {inlineSuggestionState.isAiGenerated &&
          inlineSuggestionState.hasMissingDetail ? (
            <button
              type="button"
              className="dbq-inline-suggestion-action-btn"
              onClick={() => onOpenInlineSuggestionDetail?.(fieldKey)}
              title="Complete the missing detail for this suggestion"
            >
              Complete missing detail
            </button>
          ) : null}
          {inlineSuggestionState.isAiGenerated ? (
            <button
              type="button"
              className="dbq-inline-suggestion-action-btn dbq-inline-suggestion-action-btn--evidence"
              onClick={() => onOpenInlineSuggestionEvidence?.(fieldKey)}
              disabled={!inlineSuggestionState.hasEvidence}
              title={
                inlineSuggestionState.hasEvidence
                  ? 'View page-level evidence for this AI suggestion'
                  : 'No page-level evidence was captured for this AI suggestion'
              }
            >
              View Evidence
            </button>
          ) : null}
          {inlineSuggestionState.hasRejectedSuggestions ? (
            <button
              type="button"
              className="dbq-inline-suggestion-action-btn dbq-inline-suggestion-action-btn--rejected"
              onClick={() => onOpenInlineRejectedSuggestions?.(fieldKey)}
              title="View rejected suggestions for this field"
            >
              Rejected Suggestions
            </button>
          ) : null}
        </div>
      ) : null}
      {field.description && (
        <div className="dbq-field-description small mb-3">
          {field.description}
        </div>
      )}
      {renderInput()}
      {inlineSuggestionState?.helpText ? (
        <Form.Text className="dbq-inline-suggestion-help mt-2 d-block">
          {inlineSuggestionState.helpText}
        </Form.Text>
      ) : null}
      {field.notes && (
        <Form.Text className="dbq-field-notes mt-2 d-block fst-italic">
          {field.notes}
        </Form.Text>
      )}
    </Form.Group>
  );
};

// Render static text element
export const DbqStaticText = ({
  element,
  instancePath = [],
  parentDisabled = false,
  formContext,
}) => {
  const { isVisible } = formContext;

  const isConditionMet = isVisible(element.visible_if, instancePath);
  const shouldRender = isConditionMet || !!element.code;

  if (!shouldRender) {
    return null;
  }

  const isDisabled = parentDisabled || (!isConditionMet && !!element.code);

  const styleMap = {
    heading: 'h4',
    subheading: 'h5',
    instruction: 'div',
    note: 'div',
    paragraph: 'p',
  };

  const Tag = styleMap[element.style] || 'div';

  let className = 'mb-3';
  if (isDisabled) className += ' opacity-50';
  if (element.style === 'note') className += ' dbq-note-banner p-3 rounded-3';
  if (element.style === 'instruction')
    className += ' dbq-instruction fst-italic';
  if (element.style === 'heading')
    className += ' mt-4 mb-3 fw-bold text-primary';
  if (element.style === 'subheading') className += ' mt-3 mb-2 fw-semibold';

  return (
    <Tag key={element.id} className={className}>
      {element.style === 'note' && (
        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
      )}
      {element.text}
    </Tag>
  );
};

// Render a field group
export const DbqFieldGroup = ({
  group,
  instancePath = [],
  parentDisabled = false,
  formContext,
  renderWithGroupMap = {},
  isAdmin = false,
  canComment = false,
  canSuggest = false,
  onCommentClick,
  onSuggestionClick,
  activeCommentFieldId,
  activeSuggestionFieldId,
  requiredFields = [],
  onToggleRequired,
  onFieldChangeWithConfirmation,
}) => {
  const { isVisible } = formContext;

  const isConditionMet = isVisible(group.visible_if, instancePath);
  const shouldRender = isConditionMet || !!group.code;

  if (!shouldRender) {
    return null;
  }

  const isDisabled = parentDisabled || (!isConditionMet && !!group.code);

  // Get elements that should render with this group
  const additionalElements = renderWithGroupMap[group.id] || [];

  // Determine layout mode - horizontal for side-by-side left/right fields
  const isHorizontal = group.layout === 'horizontal';
  const isTable = group.layout === 'table';

  // For horizontal layout, split elements into columns (typically 2 for left/right)
  const renderElements = (elements) => {
    if (!elements?.length) return null;

    if (isHorizontal) {
      return (
        <div className="dbq-horizontal-layout">
          {elements.map((element) => (
            <div key={element.id} className="dbq-horizontal-item">
              <DbqElement
                element={element}
                instancePath={instancePath}
                skipRenderWithGroup={false}
                parentDisabled={isDisabled}
                formContext={formContext}
                renderWithGroupMap={renderWithGroupMap}
                isAdmin={isAdmin}
                canComment={canComment}
                canSuggest={canSuggest}
                onCommentClick={onCommentClick}
                onSuggestionClick={onSuggestionClick}
                activeCommentFieldId={activeCommentFieldId}
                activeSuggestionFieldId={activeSuggestionFieldId}
                requiredFields={requiredFields}
                onToggleRequired={onToggleRequired}
                onFieldChangeWithConfirmation={onFieldChangeWithConfirmation}
              />
            </div>
          ))}
        </div>
      );
    }

    if (isTable) {
      return (
        <div className="dbq-table-layout">
          {elements.map((element) => (
            <div key={element.id} className="dbq-table-item">
              <DbqElement
                element={element}
                instancePath={instancePath}
                skipRenderWithGroup={false}
                parentDisabled={isDisabled}
                formContext={formContext}
                renderWithGroupMap={renderWithGroupMap}
                isAdmin={isAdmin}
                canComment={canComment}
                canSuggest={canSuggest}
                onCommentClick={onCommentClick}
                onSuggestionClick={onSuggestionClick}
                activeCommentFieldId={activeCommentFieldId}
                activeSuggestionFieldId={activeSuggestionFieldId}
                requiredFields={requiredFields}
                onToggleRequired={onToggleRequired}
                onFieldChangeWithConfirmation={onFieldChangeWithConfirmation}
              />
            </div>
          ))}
        </div>
      );
    }

    // Default vertical layout
    return elements.map((element) => (
      <DbqElement
        key={element.id}
        element={element}
        instancePath={instancePath}
        skipRenderWithGroup={false}
        parentDisabled={isDisabled}
        formContext={formContext}
        renderWithGroupMap={renderWithGroupMap}
        isAdmin={isAdmin}
        canComment={canComment}
        canSuggest={canSuggest}
        onCommentClick={onCommentClick}
        onSuggestionClick={onSuggestionClick}
        activeCommentFieldId={activeCommentFieldId}
        activeSuggestionFieldId={activeSuggestionFieldId}
        requiredFields={requiredFields}
        onToggleRequired={onToggleRequired}
        onFieldChangeWithConfirmation={onFieldChangeWithConfirmation}
      />
    ));
  };

  return (
    <div
      className={`dbq-group-card mb-4 animate-fade-in ${isDisabled ? 'opacity-75' : ''} ${isHorizontal ? 'dbq-group-horizontal' : ''} ${isTable ? 'dbq-group-table' : ''}`}
      key={group.id}
    >
      <div className="mb-3 border-bottom border-secondary border-opacity-25 pb-2">
        <strong className="d-block h5 mb-1">{group.label}</strong>
        {group.description && (
          <div className="dbq-field-description small">{group.description}</div>
        )}
      </div>
      <div className={`ps-2 ${isHorizontal ? 'dbq-horizontal-container' : ''}`}>
        {/* Render group's own elements */}
        {renderElements(group.elements)}

        {/* Render elements that specify render_with_group for this group */}
        {additionalElements.length > 0 && (
          <div className={isHorizontal ? 'dbq-horizontal-additional mt-3' : ''}>
            {additionalElements.map((element) => (
              <DbqElement
                key={element.id}
                element={element}
                instancePath={instancePath}
                skipRenderWithGroup={true}
                parentDisabled={isDisabled}
                formContext={formContext}
                renderWithGroupMap={renderWithGroupMap}
                isAdmin={isAdmin}
                canComment={canComment}
                canSuggest={canSuggest}
                onCommentClick={onCommentClick}
                onSuggestionClick={onSuggestionClick}
                activeCommentFieldId={activeCommentFieldId}
                activeSuggestionFieldId={activeSuggestionFieldId}
                requiredFields={requiredFields}
                onToggleRequired={onToggleRequired}
                onFieldChangeWithConfirmation={onFieldChangeWithConfirmation}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Render a repeating element (group or field)
export const DbqRepeatingElement = ({
  element,
  instancePath = [],
  parentDisabled = false,
  formContext,
  renderWithGroupMap = {},
  isAdmin = false,
  canComment = false,
  canSuggest = false,
  onCommentClick,
  onSuggestionClick,
  activeCommentFieldId,
  activeSuggestionFieldId,
  requiredFields = [],
  onToggleRequired,
  onFieldChangeWithConfirmation,
}) => {
  const {
    isVisible,
    repeatingInstances,
    handleAddRepeatingInstance,
    handleRemoveRepeatingInstance,
  } = formContext;

  const isConditionMet = isVisible(element.visible_if, instancePath);
  const shouldRender = isConditionMet || !!element.code;

  if (!shouldRender) {
    return null;
  }

  const isDisabled = parentDisabled || (!isConditionMet && !!element.code);
  const instances = repeatingInstances[element.id] || [];
  const minItems = Math.max(parseInt(element.min_items) || 1, 1);
  const maxItems = parseInt(element.max_items) || 0; // 0 means unlimited
  // Structural changes (adding/removing instances) follow the same rule as
  // field edits: free until the request is FINALIZED (edits are recorded
  // server-side while signed), frozen after.
  const finalizedLocked = Boolean(formContext?.finalizedLocked);
  const canAdd =
    !isDisabled &&
    !finalizedLocked &&
    (maxItems === 0 || instances.length < maxItems);
  const canRemove =
    !isDisabled && !finalizedLocked && instances.length > minItems;
  const headerLabel = element.label || 'Repeated Element';
  const entryLabel = element.repeat_label || element.label || 'Entry';

  // Get elements that should render with this repeating group
  const additionalElements = renderWithGroupMap[element.id] || [];

  return (
    <div
      className={`dbq-repeating-wrapper mb-4 animate-fade-in ${isDisabled ? 'opacity-75' : ''}`}
      key={`${element.id}-repeating`}
    >
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <strong className="h5 mb-1 d-block">{headerLabel}</strong>
          {element.description && (
            <div className="dbq-field-description small mb-2">
              {element.description}
            </div>
          )}
          <Badge bg="info" className="mt-1">
            {instances.length} of {maxItems === 0 ? '∞' : maxItems}
            {minItems > 0 && ` (min: ${minItems})`}
          </Badge>
        </div>
      </div>

      {instances.length === 0 && (
        <Alert variant="info" className="mb-0 dbq-alert-info">
          No entries yet. Use "Add" to create the first one.
        </Alert>
      )}

      {instances.map((instanceId, index) => {
        const childInstancePath = [...instancePath, instanceId];
        const instanceTitle = `${entryLabel} #${index + 1}`;

        return (
          <div
            className="dbq-repeating-instance mb-3 animate-fade-in"
            key={instanceId}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center gap-2">
                <Badge
                  bg="primary"
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: '32px', height: '32px', fontSize: '0.9rem' }}
                >
                  {index + 1}
                </Badge>
                <span className="fw-semibold">{instanceTitle}</span>
              </div>
              {canRemove && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() =>
                    handleRemoveRepeatingInstance(element, instanceId)
                  }
                  className="d-flex align-items-center gap-2"
                  disabled={isDisabled}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  Remove
                </Button>
              )}
            </div>
            <div className="ps-2">
              {element.type === 'group' && element.elements ? (
                element.elements.map((child) => (
                  <DbqElement
                    key={child.id}
                    element={child}
                    instancePath={childInstancePath}
                    skipRenderWithGroup={false}
                    parentDisabled={isDisabled}
                    formContext={formContext}
                    renderWithGroupMap={renderWithGroupMap}
                    isAdmin={isAdmin}
                    canComment={canComment}
                    canSuggest={canSuggest}
                    onCommentClick={onCommentClick}
                    onSuggestionClick={onSuggestionClick}
                    activeCommentFieldId={activeCommentFieldId}
                    activeSuggestionFieldId={activeSuggestionFieldId}
                    requiredFields={requiredFields}
                    onToggleRequired={onToggleRequired}
                    onFieldChangeWithConfirmation={
                      onFieldChangeWithConfirmation
                    }
                  />
                ))
              ) : element.type === 'field' ? (
                <DbqField
                  field={element}
                  instancePath={childInstancePath}
                  parentDisabled={isDisabled}
                  formContext={formContext}
                  renderWithGroupMap={renderWithGroupMap}
                  isAdmin={isAdmin}
                  canComment={canComment}
                  canSuggest={canSuggest}
                  onCommentClick={onCommentClick}
                  onSuggestionClick={onSuggestionClick}
                  activeCommentFieldId={activeCommentFieldId}
                  activeSuggestionFieldId={activeSuggestionFieldId}
                  requiredFields={requiredFields}
                  onToggleRequired={onToggleRequired}
                  onFieldChangeWithConfirmation={onFieldChangeWithConfirmation}
                />
              ) : null}
            </div>
          </div>
        );
      })}

      {/* Render elements that specify render_with_group for this repeating group */}
      {additionalElements.length > 0 && (
        <div className="mt-3">
          {additionalElements.map((el) => (
            <DbqElement
              key={el.id}
              element={el}
              instancePath={instancePath}
              skipRenderWithGroup={true}
              parentDisabled={isDisabled}
              formContext={formContext}
              renderWithGroupMap={renderWithGroupMap}
              isAdmin={isAdmin}
              canComment={canComment}
              canSuggest={canSuggest}
              onCommentClick={onCommentClick}
              onSuggestionClick={onSuggestionClick}
              activeCommentFieldId={activeCommentFieldId}
              activeSuggestionFieldId={activeSuggestionFieldId}
              requiredFields={requiredFields}
              onToggleRequired={onToggleRequired}
              onFieldChangeWithConfirmation={onFieldChangeWithConfirmation}
            />
          ))}
        </div>
      )}

      {/* Add button at the bottom */}
      {canAdd && (
        <div className="text-center mt-3">
          <Button
            variant="outline-success"
            size="sm"
            onClick={() => handleAddRepeatingInstance(element)}
            className="d-flex align-items-center gap-2 mx-auto"
            disabled={isDisabled}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add {entryLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

// Render any element (field, group, or static text)
export const DbqElement = ({
  element,
  instancePath = [],
  skipRenderWithGroup = false,
  parentDisabled = false,
  formContext,
  renderWithGroupMap = {},
  isAdmin = false,
  canComment = false,
  canSuggest = false,
  onCommentClick,
  onSuggestionClick,
  activeCommentFieldId,
  activeSuggestionFieldId,
  requiredFields = [],
  onToggleRequired,
  onFieldChangeWithConfirmation,
}) => {
  if (!element || !element.type) return null;

  // Skip elements that should be rendered with a group (unless we're explicitly rendering them)
  if (!skipRenderWithGroup && element.render_with_group) {
    return null;
  }

  // Handle repeating elements
  if (element.repeating) {
    return (
      <DbqRepeatingElement
        element={element}
        instancePath={instancePath}
        parentDisabled={parentDisabled}
        formContext={formContext}
        renderWithGroupMap={renderWithGroupMap}
        isAdmin={isAdmin}
        canComment={canComment}
        canSuggest={canSuggest}
        onCommentClick={onCommentClick}
        onSuggestionClick={onSuggestionClick}
        activeCommentFieldId={activeCommentFieldId}
        activeSuggestionFieldId={activeSuggestionFieldId}
        requiredFields={requiredFields}
        onToggleRequired={onToggleRequired}
        onFieldChangeWithConfirmation={onFieldChangeWithConfirmation}
      />
    );
  }

  switch (element.type) {
    case 'field':
      return (
        <DbqField
          field={element}
          instancePath={instancePath}
          parentDisabled={parentDisabled}
          formContext={formContext}
          renderWithGroupMap={renderWithGroupMap}
          isAdmin={isAdmin}
          canComment={canComment}
          canSuggest={canSuggest}
          onCommentClick={onCommentClick}
          onSuggestionClick={onSuggestionClick}
          activeCommentFieldId={activeCommentFieldId}
          activeSuggestionFieldId={activeSuggestionFieldId}
          requiredFields={requiredFields}
          onToggleRequired={onToggleRequired}
          onFieldChangeWithConfirmation={onFieldChangeWithConfirmation}
        />
      );
    case 'group':
      return (
        <DbqFieldGroup
          group={element}
          instancePath={instancePath}
          parentDisabled={parentDisabled}
          formContext={formContext}
          renderWithGroupMap={renderWithGroupMap}
          isAdmin={isAdmin}
          canComment={canComment}
          canSuggest={canSuggest}
          onCommentClick={onCommentClick}
          onSuggestionClick={onSuggestionClick}
          activeCommentFieldId={activeCommentFieldId}
          activeSuggestionFieldId={activeSuggestionFieldId}
          requiredFields={requiredFields}
          onToggleRequired={onToggleRequired}
          onFieldChangeWithConfirmation={onFieldChangeWithConfirmation}
        />
      );
    case 'static_text':
      return (
        <DbqStaticText
          element={element}
          instancePath={instancePath}
          parentDisabled={parentDisabled}
          formContext={formContext}
        />
      );
    default:
      return null;
  }
};

// Render a section
export const DbqSection = ({
  section,
  formContext,
  renderWithGroupMap = {},
  isAdmin = false,
  canComment = false,
  canSuggest = false,
  readOnly = false,
  onCommentClick,
  onSuggestionClick,
  activeCommentFieldId,
  activeSuggestionFieldId,
  requiredFields = [],
  onToggleRequired,
  onFieldChangeWithConfirmation,
}) => {
  const { isVisible } = formContext;

  if (!isVisible(section.visible_if, [])) {
    return null;
  }

  return (
    <div className="dbq-section-card mb-5 animate-fade-in" key={section.id}>
      <div className="dbq-section-header">
        {section.code && (
          <span className="dbq-section-code">{section.code}</span>
        )}
        <h3 className="dbq-section-title mb-0">{section.label}</h3>
      </div>

      {section.description && (
        <div className="dbq-info-banner mb-4">{section.description}</div>
      )}

      <div className="dbq-section-content">
        {section.elements?.map((element) => (
          <DbqElement
            key={element.id}
            element={element}
            instancePath={[]}
            skipRenderWithGroup={false}
            parentDisabled={readOnly}
            formContext={formContext}
            renderWithGroupMap={renderWithGroupMap}
            isAdmin={isAdmin}
            canComment={canComment}
            canSuggest={canSuggest}
            onCommentClick={onCommentClick}
            onSuggestionClick={onSuggestionClick}
            activeCommentFieldId={activeCommentFieldId}
            activeSuggestionFieldId={activeSuggestionFieldId}
            requiredFields={requiredFields}
            onToggleRequired={onToggleRequired}
            onFieldChangeWithConfirmation={onFieldChangeWithConfirmation}
          />
        ))}
      </div>
    </div>
  );
};

// Main form renderer component
export const DbqFormView = ({
  formJson,
  formContext,
  renderWithGroupMap = {},
  isAdmin = false,
  canComment = false,
  canSuggest = false,
  readOnly = false,
  isSectionReadOnly = null,
  onCommentClick,
  onSuggestionClick,
  activeCommentFieldId,
  activeSuggestionFieldId,
  requiredFields = [],
  onToggleRequired,
  onFieldChangeWithConfirmation,
  renderAfterSection,
}) => {
  if (!formJson?.sections) {
    return null;
  }

  return (
    <>
      {formJson.sections.map((section, index) => {
        const sectionReadOnly =
          typeof isSectionReadOnly === 'function'
            ? isSectionReadOnly(section)
            : readOnly;
        const sectionVisible =
          typeof formContext?.isVisible === 'function'
            ? formContext.isVisible(section.visible_if, [])
            : true;

        return (
          <React.Fragment key={section.id}>
            <DbqSection
              section={section}
              formContext={formContext}
              renderWithGroupMap={renderWithGroupMap}
              isAdmin={isAdmin}
              canComment={canComment}
              canSuggest={canSuggest}
              readOnly={sectionReadOnly}
              onCommentClick={onCommentClick}
              onSuggestionClick={onSuggestionClick}
              activeCommentFieldId={activeCommentFieldId}
              activeSuggestionFieldId={activeSuggestionFieldId}
              requiredFields={requiredFields}
              onToggleRequired={onToggleRequired}
              onFieldChangeWithConfirmation={onFieldChangeWithConfirmation}
            />
            {sectionVisible && typeof renderAfterSection === 'function'
              ? renderAfterSection(section, index)
              : null}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default DbqFormView;
